import { Replay } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import Hls from 'hls.js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import defaultVideoPlayerStyles from '../../styles';
import { HLSVideoPlayerProps } from './types';

type Props = Omit<HLSVideoPlayerProps, 'streamType'>;

const HLSVideoPlayer: React.FunctionComponent<Props> = (props) => {
  const { url, className, controls, setShowPTZControls, retry = true, setStreamLoaded } = props;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const classes = defaultVideoPlayerStyles();
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(true);
  const [isStreamAvailable, setIsStreamAvailable] = useState(true);
  const [reload, setReload] = useState(false);

  useEffect(() => {
    let toggleStreamLoaded: ReturnType<typeof setTimeout>;
    if (!loading) {
      toggleStreamLoaded = setTimeout(() => setStreamLoaded?.(!loading), 700);
    } else {
      setStreamLoaded?.(!loading);
    }
    return () => toggleStreamLoaded && clearTimeout(toggleStreamLoaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (!isStreamAvailable && !reload) {
      timeout = setTimeout(() => {
        setReload(true);
      }, 1500);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isStreamAvailable, reload]);

  useEffect(() => {
    let hls: Hls;
    const _initPlayer = () => {
      setLoading(true);
      setIsStreamAvailable(true);
      if (hls !== null) {
        hls?.destroy();
      }
      const newHls = new Hls({
        maxLoadingDelay: 4,
        minAutoBitrate: 0,
        lowLatencyMode: true,
      });
      if (videoRef.current != null) {
        newHls.attachMedia(videoRef.current);
      }
      if (!url) {
        setIsStreamAvailable(false);
        setLoading(false);
      }
      newHls.on(Hls.Events.MEDIA_ATTACHED, () => {
        newHls.loadSource(url);

        newHls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsStreamAvailable(true);
          setLoading(false);
        });
      });

      newHls.on(Hls.Events.ERROR, function (event, data) {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setIsStreamAvailable(false);
              setLoading(false);
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              setIsStreamAvailable(false);
              setLoading(false);
              break;
            default:
              _initPlayer();
              break;
          }
        }
      });

      hls = newHls;
    };

    if (Hls.isSupported()) {
      _initPlayer();
    }

    if (reload) {
      _initPlayer();
      setReload(false);
    }

    return () => {
      if (hls !== null) {
        hls?.destroy();
      }
    };
  }, [url, videoRef, reload]);

  useEffect(() => {
    setShowPTZControls?.(isStreamAvailable);
  }, [isStreamAvailable, setShowPTZControls]);

  return (
    <div style={{ position: 'relative' }} className={classes.videoWrapper}>
      <video
        className={className ? className : classes.video}
        ref={videoRef}
        playsInline
        autoPlay
        muted
        controls={controls}
      />
      {loading ? (
        <div className={className ? className : classes.loading}>
          <CircularProgress />
        </div>
      ) : !isStreamAvailable ? (
        <div className={className ? className : classes.unavailableWrapper}>
          <div>
            <div className={className ? className : classes.unavailable}>
              <Typography variant='body2'>{t('streamUnavailable')}</Typography>
            </div>
            {retry && (
              <div className={className ? className : classes.retryWrapper}>
                <Tooltip title={t('retry')}>
                  <IconButton
                    size='medium'
                    className={className ? className : classes.retryButton}
                    onClick={() => setReload(true)}
                  >
                    <Replay fontSize='medium' />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(HLSVideoPlayer);
